import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getPostcardList, getPostcardItemCategory } from '../api/profileService';
import { ROUTES } from '../../../app/router/routes';
import PostcardViewModal from '../../postcard/components/PostcardViewModal';
import { useLanguage } from '@shared/contexts/LanguageContext';



function PostcardRecordCard({ item, onClickCard }) {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const imgSrc = `/image/postcard/pc_${item.item_id}.png`;
  const dateObj = new Date(item.created_at);
  const m = String(dateObj.getMonth() + 1).padStart(2, '0');
  const d = String(dateObj.getDate()).padStart(2, '0');
  const dateStr = `${m}/${d}`;

  return (
    <div className="self-stretch px-2.5 py-2 bg-gradient-to-br from-orange-100/80 via-white to-orange-100/60 rounded-lg outline outline-1 outline-offset-[-1px] outline-[#6E625B]/10 flex flex-row justify-start items-center gap-4 hover:bg-orange-50/50 transition-colors cursor-pointer"
      onClick={() => {
        if (onClickCard) {
          onClickCard(item.share_code);
        } else {
          navigate(ROUTES.POSTCARD_VIEW(item.share_code));
        }
      }}
    >
      <div className="w-12 h-12 relative overflow-hidden flex-shrink-0 bg-white rounded flex justify-center items-center shadow-sm">
        <img className="w-10 h-10 object-contain" src={imgSrc} alt={item.item_name} />
      </div>
      <div className="flex-1 grid grid-cols-1 gap-1 items-center w-full min-w-0">
        <div className="flex flex-auto items-center justify-between gap-2 overflow-hidden">
          <div className="text-[#C59B5A] text-sm font-bold font-['Noto_Sans_TC'] leading-5 text-left truncate">
            To: {item.to_name}
          </div>
          <div className={`px-2 py-0.5 rounded flex-shrink-0 border text-[10px] font-medium leading-tight ${item.is_viewed ? 'bg-[#C59B5A] text-white border-[#C59B5A]' : 'bg-transparent text-[#6E625B]/50 border-[#6E625B]/20'}`}>
            {item.is_viewed ? t('postcard-list.viewed') : t('postcard-list.notViewed')}
          </div>
        </div>
        <div className="text-[#6E625B] text-xs font-['Noto_Sans_TC'] leading-4 text-left truncate">
          {item.content}
        </div>
      </div>

      <div className="flex flex-col items-end justify-center flex-shrink-0 w-16">
        <div className="text-[#C59B5A] text-sm font-semibold
 font-['Noto_Sans_TC'] leading-5 whitespace-nowrap">
          {item.point > 0 ? `+${item.point}` : t('postcard-list.noPoints')}
        </div>
        <div className="text-[#6E625B] text-xs font-['Noto_Sans_TC'] leading-4 whitespace-nowrap">
          {dateStr}
        </div>
      </div>
    </div>
  );
}

function SearchBar({ keyword, setKeyword, filterIsViewed, setFilterIsViewed, filterItemIds, setFilterItemIds, onSearch, itemCategories = [] }) {
  const { t } = useLanguage();
  return (
    <div className="flex flex-col gap-2 w-full pt-1">
      <div className="self-stretch h-12 bg-white/75 inline-flex justify-end items-center gap-2 overflow-hidden flex-shrink-0">
        <div className="relative flex-1">
          <input
            type="text"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && onSearch()}
            placeholder={t('postcard-list.searchPlaceholder')}
            className="w-full h-8 bg-white rounded-[30px] border border-[#6E625B]/10 pl-3 pr-8 text-base font-light font-['Noto_Sans_TC'] text-[#6E625B] placeholder-[#6E625B]/40 focus:outline-none focus:border-[#C59B5A]/40 transition-colors"
          />
        </div>
        <button
          onClick={onSearch}
          className="w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center hover:opacity-70 transition-opacity bg-[#C59B5A] text-white"
          aria-label={t('postcard-list.searchAria')}
        >
          <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
            <circle cx="10" cy="10" r="5.75" stroke="currentColor" strokeWidth="1.5" />
            <path d="M14 14L18 18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </button>
      </div>
      <div className="flex gap-2 w-full">
        <select
          value={filterIsViewed}
          onChange={(e) => setFilterIsViewed(e.target.value)}
          className="flex-1 h-8 bg-white rounded border border-[#6E625B]/10 px-2 text-sm text-[#6E625B] focus:outline-none focus:border-[#C59B5A]/40"
        >
          <option value="">{t('postcard-list.filterAll')}</option>
          <option value="true">{t('postcard-list.filterViewed')}</option>
          <option value="false">{t('postcard-list.filterNotViewed')}</option>
        </select>
        <select
          value={filterItemIds}
          onChange={(e) => setFilterItemIds(e.target.value)}
          className="flex-1 h-8 bg-white rounded border border-[#6E625B]/10 px-2 text-sm text-[#6E625B] focus:outline-none focus:border-[#C59B5A]/40"
        >
          <option value="">{t('postcard-list.filterAllCards')}</option>
          {itemCategories.map((item, idx) => (
            <option key={idx} value={item.item_ids.join(',')}>{item.category}</option>
          ))}
        </select>
      </div>
    </div>
  );
}

function PostcardList({ isOpen, onClose }) {
  const { t } = useLanguage();
  const [selectedShareCode, setSelectedShareCode] = useState(null);
  const [keyword, setKeyword] = useState('');
  const [activeKeyword, setActiveKeyword] = useState('');
  const [filterIsViewed, setFilterIsViewed] = useState('');
  const [filterItemIds, setFilterItemIds] = useState('');
  const [itemCategories, setItemCategories] = useState([]);

  const [page, setPage] = useState(1);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPoints, setTotalPoints] = useState(0);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const listRef = useRef(null);

  const PER_PAGE = 10;

  useEffect(() => {
    getPostcardItemCategory()
      .then((res) => {
        if (res?.items) {
          setItemCategories(res.items);
        }
      })
      .catch((err) => console.error('Failed to fetch categories:', err));
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    setSelectedShareCode(null);
    setKeyword('');
    setActiveKeyword('');
    setFilterIsViewed('');
    setFilterItemIds('');
    setPage(1);
    setItems([]);
    setHasMore(false);
    fetchPage(1, '', '', '');
  }, [isOpen]);

  const fetchPage = useCallback(
    async (targetPage, kw, isViewed, itemIds) => {
      setLoading(true);

      try {
        const payload = {
          page: targetPage,
          per_page: PER_PAGE,
          ...(kw && { keyword: kw }),
          ...(isViewed && { is_viewed: isViewed }),
        };

        if (isViewed === 'true') payload.is_viewed = true;
        if (isViewed === 'false') payload.is_viewed = false;
        if (itemIds) payload.item_ids = itemIds.split(',').map(Number);
        const res = await getPostcardList(payload);
        const newItems = res?.items ?? [];
        const totalCount = res?.total_items ?? newItems.length;

        setItems((prev) => (targetPage === 1 ? newItems : [...prev, ...newItems]));
        setTotal(totalCount);
        setTotalPoints(res?.total_points ?? 0);
        setHasMore(targetPage * PER_PAGE < totalCount);
        setPage(targetPage);
      } catch (err) {
        console.error('PostcardList fetch error:', err);
        if (targetPage === 1) setItems([]);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const handleSearch = useCallback(() => {
    setActiveKeyword(keyword);
    setPage(1);
    setItems([]);
    fetchPage(1, keyword, filterIsViewed, filterItemIds);
  }, [keyword, filterIsViewed, filterItemIds, fetchPage]);

  // Listen to filter changes independently to trigger search
  useEffect(() => {
    if (isOpen && (filterIsViewed !== '' || filterItemIds !== '' || keyword === '')) {
      setActiveKeyword(keyword);
      setPage(1);
      setItems([]);
      fetchPage(1, keyword, filterIsViewed, filterItemIds);
    }
  }, [filterIsViewed, filterItemIds]);

  const handleScroll = (e) => {
    const bottom = e.target.scrollHeight - e.target.scrollTop <= e.target.clientHeight + 10;
    if (bottom && !loading && hasMore) {
      fetchPage(page + 1, activeKeyword, filterIsViewed, filterItemIds);
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      <div
        className="fixed inset-0 z-100 flex items-center justify-center p-4 sm:p-6"
        style={{ background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(2px)' }}
        onClick={handleBackdropClick}
      >
        <div
          className="
          relative bg-white rounded-[20px] flex flex-col gap-4 overflow-hidden
          w-full max-w-sm
          h-[90vh] max-h-[785px]
          p-6
          shadow-[0px_11px_23px_0px_rgba(0,0,0,0.10),0px_42px_42px_0px_rgba(0,0,0,0.09),0px_95px_57px_0px_rgba(0,0,0,0.05)]
        "
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-baseline justify-between flex-shrink-0">
            <div className="text-[#171717]/95 text-xl sm:text-2xl font-bold font-['Noto_Serif_TC'] leading-7">
              {t('postcard-list.header')}
            </div>
            <button
              onClick={onClose}
              aria-label={t('postcard-list.closeAria')}
              className="w-6 h-6 flex items-center justify-center flex-shrink-0 hover:opacity-60 transition-opacity"
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <circle cx="10" cy="10" r="9.25" stroke="rgba(110,98,91,0.30)" strokeWidth="1.5" />
                <path d="M7 7L13 13M13 7L7 13" stroke="rgba(110,98,91,0.30)" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </button>
          </div>

          <div className="flex justify-between items-center bg-[#FDFBF9] py-2 px-3 rounded-lg text-[#6E625B] border border-[#6E625B]/10">
            <div className="text-sm">{t('postcard-list.totalRecords').replace('{{count}}', total)}</div>
            <div className="text-sm font-bold text-[#BC313D]">{t('postcard-list.totalPoints').replace('{{points}}', totalPoints)}</div>
          </div>

          <SearchBar
            keyword={keyword}
            setKeyword={setKeyword}
            filterIsViewed={filterIsViewed}
            setFilterIsViewed={setFilterIsViewed}
            filterItemIds={filterItemIds}
            setFilterItemIds={setFilterItemIds}
            onSearch={handleSearch}
            itemCategories={itemCategories}
          />

          <div ref={listRef} onScroll={handleScroll} className="flex-1 overflow-y-auto -mx-1 px-1">
            {items.length === 0 && !loading ? (
              <div className="flex flex-col items-center justify-center gap-2 py-16">
                <svg width="40" height="40" viewBox="0 0 40 40" fill="none" opacity="0.3">
                  <circle cx="18" cy="18" r="11.5" stroke="#6E625B" strokeWidth="2" />
                  <path d="M27 27L35 35" stroke="#6E625B" strokeWidth="2" strokeLinecap="round" />
                </svg>
                <p className="text-[#6E625B]/40 text-sm font-['Noto_Sans_TC']">
                  {activeKeyword || filterIsViewed || filterItemIds ? t('postcard-list.noResults') : t('postcard-list.noRecords')}
                </p>
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                {items.map((item, idx) => (
                  <PostcardRecordCard
                    key={item.record_id ?? idx}
                    item={item}
                    onClickCard={(code) => setSelectedShareCode(code)}
                  />
                ))}
                {loading && (
                  <div className="flex flex-col gap-2">
                    {[...Array(3)].map((_, i) => (
                      <div
                        key={`skeleton-${i}`}
                        className="h-[68px] rounded-lg bg-orange-50/60 animate-pulse"
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      {!!selectedShareCode && (
        <PostcardViewModal
          isOpen={!!selectedShareCode}
          onClose={() => setSelectedShareCode(null)}
          shareCode={selectedShareCode}
        />
      )}
    </>
  );
}


export default PostcardList;
